import { useEffect, useState } from 'react';

/*
 * sandyPanelStore (Sandy 2026-08-07): ONE open/closed flag and ONE floating
 * rect shared by every host the chat can live in (the shell's aside rail,
 * Intervene's bottom dock, the detached floating window). Hosts subscribe
 * through useSandyPanel, so closing Sandy on any page closes him everywhere
 * and the corner pill never doubles up.
 *
 * localStorage keys:
 *   tdv3.sandyOpen     '1'/'0' — open vs collapsed to the pill (predates this
 *                      store; the shell wrote it directly before)
 *   tdv3.sandy.float   JSON {x,y,w,h} while detached; absent when docked
 *   tdv3.sandy.asideW  px width of the shell's aside panel
 *   tdv3.sandy.dockH   px height of Intervene's dock
 */

const KEYS = {
  open: 'tdv3.sandyOpen',
  float: 'tdv3.sandy.float',
  asideW: 'tdv3.sandy.asideW',
  dockH: 'tdv3.sandy.dockH',
};
const EVT = 'tdv3:sandyPanelState';

const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => {
  try {
    if (v == null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch { /* private mode */ }
};

// One-time migration: an earlier pass tracked collapse under its own key
// (tdv3.sandy.collapsed); fold it into tdv3.sandyOpen and drop it so there
// is exactly one source of truth.
const legacy = read('tdv3.sandy.collapsed');
if (legacy != null) {
  if (legacy === '1') write(KEYS.open, '0');
  write('tdv3.sandy.collapsed', null);
}

// Keep the floating window on screen: never smaller than a usable chat,
// never wider than the viewport, grab bar always reachable.
function clampRect(r) {
  const vw = window.innerWidth || 1280;
  const vh = window.innerHeight || 800;
  const w = Math.round(Math.min(Math.max(r.w, 320), vw * 0.94));
  const h = Math.round(Math.min(Math.max(r.h, 380), vh * 0.9));
  const x = Math.round(Math.max(8, Math.min(r.x, vw - w - 8)));
  const y = Math.round(Math.max(8, Math.min(r.y, vh - 72)));
  return { x, y, w, h };
}

function readFloat() {
  try {
    const r = JSON.parse(read(KEYS.float));
    if (r && ['x', 'y', 'w', 'h'].every((k) => typeof r[k] === 'number')) return clampRect(r);
  } catch { /* bad JSON */ }
  return null;
}

let open = read(KEYS.open) !== '0';
let float = readFloat();

const emit = () => window.dispatchEvent(new Event(EVT));

export function setSandyOpen(v) {
  open = !!v;
  write(KEYS.open, open ? '1' : '0');
  // Closing also docks him, so the pill always reopens the panel in its host.
  if (!open && float) {
    float = null;
    write(KEYS.float, null);
  }
  emit();
}

// `persist: false` is for mid-drag frames — the position still broadcasts so
// the window follows the pointer, but localStorage only sees the final spot.
export function setSandyFloat(rect, persist = true) {
  float = rect ? clampRect(rect) : null;
  if (persist) write(KEYS.float, float ? JSON.stringify(float) : null);
  emit();
}

// Size changes from the floating window's native resize handle: keep the
// stored rect in sync WITHOUT emitting — the element already has this size,
// and re-rendering mid-drag would fight the browser's resize.
export function setSandyFloatSize(w, h) {
  if (!float || (float.w === w && float.h === h)) return;
  float = { ...float, w, h };
  write(KEYS.float, JSON.stringify(float));
}

// Persisted panel sizes ('asideW' | 'dockH'). No event: each size has exactly
// one host, which owns it as local state and just writes it through here.
export function getSandySize(which, fallback) {
  const n = parseInt(read(KEYS[which]), 10);
  return Number.isFinite(n) ? n : fallback;
}
export function setSandySize(which, px) {
  write(KEYS[which], String(Math.round(px)));
}

// Detach-drag shared by both docked hosts: past a 6px threshold the panel
// pops out at (a capped version of) its current on-screen rect and follows
// the pointer until release. Window-level listeners, so the drag survives
// the docked -> floating re-render that unmounts the element it started on.
export function beginSandyDetach(e, panelEl) {
  if (e.button !== 0 || !panelEl) return;
  const rect = panelEl.getBoundingClientRect();
  const start = { x: e.clientX, y: e.clientY };
  // Cap the popped-out size so a full-height aside doesn't become a
  // full-height window.
  const w = Math.round(Math.min(rect.width, 440));
  const h = Math.round(Math.min(rect.height, (window.innerHeight || 800) * 0.7, 640));
  const grabX = Math.min(e.clientX - rect.left, w - 48);
  const grabY = Math.min(e.clientY - rect.top, 40);
  let live = false;
  const place = (ev) => ({ x: ev.clientX - grabX, y: ev.clientY - grabY, w, h });
  const move = (ev) => {
    if (!live && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < 6) return;
    live = true;
    setSandyFloat(place(ev), false);
  };
  const up = (ev) => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    if (live) setSandyFloat(place(ev));
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  e.preventDefault();
}

// Move an already-floating window by its grab bar.
export function beginSandyMove(e) {
  if (e.button !== 0 || !float) return;
  if (e.target.closest && e.target.closest('button')) return; // the bar's X
  const { w, h } = float;
  const grabX = e.clientX - float.x;
  const grabY = e.clientY - float.y;
  const place = (ev) => ({ x: ev.clientX - grabX, y: ev.clientY - grabY, w, h });
  const move = (ev) => setSandyFloat(place(ev), false);
  const up = (ev) => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    setSandyFloat(place(ev));
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  e.preventDefault();
}

export function useSandyPanel() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const on = () => setTick((t) => t + 1);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  return { open, float, setOpen: setSandyOpen, setFloat: setSandyFloat };
}
